import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Loader2, Send, CheckCircle2, AlertCircle, LogIn, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, floorLabel, gbp } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUnit } from "@/context/UnitContext";
import { dimPairLabel } from "@/lib/units";

/**
 * Account-gated "send request to nearby retailers" review step (Stage 4).
 *
 * Props:
 *   rooms: [{ name, width_m, length_m, flooring_type, colour }]
 *   postcode: string
 *   projectReference?: string
 *   source?: string
 *   onClose: () => void
 *   onSent?: (result) => void
 */
export default function SendRequestModal({ rooms = [], postcode = "", projectReference, source = "whole_home", onClose, onSent }) {
  const { user, loading, resendVerification } = useAuth();
  const { unit } = useUnit();
  const nav = useNavigate();
  const [actively, setActively] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [done, setDone] = useState(null); // { created, skipped }

  const validRooms = rooms.filter((r) => Number(r.width_m) > 0 && Number(r.length_m) > 0 && r.flooring_type);
  const verified = user && user.role === "customer" && user.email_verified !== false;

  function goSignIn() {
    const next = window.location.pathname + window.location.search;
    // Remember the intent so we can bring the shopper straight back to this
    // review step after they sign in / verify. The room plan itself is
    // preserved by the page (whole-home: localStorage, search: URL params).
    try { sessionStorage.setItem("fm_request_intent", source || "1"); } catch (e) { /* ignore */ }
    onClose?.();
    nav(`/account?next=${encodeURIComponent(next)}`);
  }

  async function handleResend() {
    setResending(true);
    const res = await resendVerification();
    setResending(false);
    if (res.ok) toast.success("Verification email sent");
    else toast.error(res.error || "Couldn't send email");
  }

  async function send() {
    if (!actively) {
      toast.error("Please confirm you're actively looking.");
      return;
    }
    if (!postcode || postcode.trim().length < 3) {
      toast.error("A postcode is needed so we can match nearby retailers.");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/requests", {
        postcode: postcode.trim(),
        rooms: validRooms.map((r) => ({
          name: r.name || "Room",
          width_m: Number(r.width_m),
          length_m: Number(r.length_m),
          flooring_type: r.flooring_type,
          colour: r.colour || null,
        })),
        note: note.trim() || null,
        actively_looking: true,
        project_reference: projectReference || null,
        source,
      });
      setDone(res.data);
      onSent?.(res.data);
      if ((res.data.created || []).length > 0) {
        toast.success(`Request sent for ${res.data.created.length} room${res.data.created.length !== 1 ? "s" : ""}`);
      } else {
        toast.info("You've already sent these requests.");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" role="dialog" aria-modal="true" data-testid="send-request-modal" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg text-slate-900">Send request to nearby retailers</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="close-request-modal"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          {loading ? (
            <div className="py-10 grid place-items-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : done ? (
            <div className="text-center py-6" data-testid="request-sent-state">
              <CheckCircle2 className="w-12 h-12 text-[#2A5A3B] mx-auto" />
              <h4 className="font-display font-bold text-xl text-slate-900 mt-3">
                {(done.created || []).length > 0 ? "Request sent" : "You're already set up"}
              </h4>
              <p className="text-sm text-slate-600 mt-2">
                {(done.created || []).length > 0
                  ? `We've let nearby retailers know you're looking for ${done.created.length} room${done.created.length !== 1 ? "s" : ""} of flooring. Your details stay private — retailers only see the room size and type.`
                  : "You already have active requests for these rooms."}
              </p>
              {(done.skipped || []).length > 0 && (
                <p className="text-xs text-amber-700 mt-2">{done.skipped.length} room{done.skipped.length !== 1 ? "s were" : " was"} skipped — you already have an active request for {done.skipped.length !== 1 ? "those" : "that"}.</p>
              )}
              <div className="mt-5 flex flex-col sm:flex-row gap-2 justify-center">
                <button onClick={() => { onClose?.(); nav("/account"); }} className="h-11 px-5 rounded-full bg-[#0F3D2E] text-white font-semibold text-sm" data-testid="view-my-requests">View my requests</button>
                <button onClick={onClose} className="h-11 px-5 rounded-full border border-slate-300 bg-white font-semibold text-sm text-slate-700">Close</button>
              </div>
            </div>
          ) : !user ? (
            <div className="text-center py-4" data-testid="request-signin-gate">
              <LogIn className="w-10 h-10 text-[#E05915] mx-auto" />
              <h4 className="font-display font-semibold text-lg text-slate-900 mt-3">Sign in to send your request</h4>
              <p className="text-sm text-slate-600 mt-2">A free FloorMatch account keeps your request private and lets you track it. Your plan is saved on this device while you sign in.</p>
              <button onClick={goSignIn} className="mt-5 h-12 px-6 rounded-full bg-[#E05915] hover:bg-[#c24b10] text-white font-semibold inline-flex items-center gap-2" data-testid="request-signin-btn">
                Sign in or create account
              </button>
            </div>
          ) : user.role !== "customer" ? (
            <div className="text-center py-6 text-slate-600" data-testid="request-not-customer">
              <AlertCircle className="w-10 h-10 text-amber-500 mx-auto" />
              <p className="text-sm mt-3">Flooring requests are for shoppers. You're signed in with a {user.role} account.</p>
            </div>
          ) : !verified ? (
            <div className="text-center py-4" data-testid="request-verify-gate">
              <AlertCircle className="w-10 h-10 text-amber-500 mx-auto" />
              <h4 className="font-display font-semibold text-lg text-slate-900 mt-3">Confirm your email first</h4>
              <p className="text-sm text-slate-600 mt-2">Please confirm your email address before sending a request. Check your inbox for the link.</p>
              <button onClick={handleResend} disabled={resending} className="mt-4 inline-flex items-center gap-2 font-semibold text-[#E05915] underline disabled:opacity-60" data-testid="request-resend-verify">
                {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Resend verification email
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <MapPin className="w-4 h-4 text-slate-400" /> Matching retailers near <span className="font-semibold text-slate-900">{postcode || "your postcode"}</span>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-xl divide-y divide-slate-100" data-testid="request-rooms-summary">
                {validRooms.map((r, i) => (
                  <div key={i} className="px-4 py-2.5 flex items-center justify-between text-sm">
                    <span className="font-semibold text-slate-800">{r.name || "Room"} <span className="font-normal text-slate-400">· {dimPairLabel(r.width_m, r.length_m, unit)}</span></span>
                    <span className="text-slate-500">{floorLabel(r.flooring_type)}{r.colour ? ` · ${r.colour}` : ""}</span>
                  </div>
                ))}
              </div>

              <label className="block">
                <span className="text-xs font-semibold text-slate-500 mb-1.5 block">Anything else? (optional — your identity stays hidden)</span>
                <textarea
                  className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E05915]/30 focus:border-[#E05915]"
                  rows={2}
                  maxLength={500}
                  placeholder="e.g. keen on a soft grey, flexible on collection"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  data-testid="request-note"
                />
                <span className="text-[11px] text-slate-400">Please don't include contact details here — retailers contact you only through FloorMatch.</span>
              </label>

              <label className="flex items-start gap-2.5 cursor-pointer bg-[#2A5A3B]/5 border border-[#2A5A3B]/15 rounded-xl px-4 py-3">
                <input type="checkbox" className="mt-0.5 w-4 h-4 accent-[#2A5A3B]" checked={actively} onChange={(e) => setActively(e.target.checked)} data-testid="request-actively-looking" />
                <span className="text-sm text-slate-700">I'm <b>actively looking</b> for this flooring and will <b>close my request once I'm sorted</b>. Happy for nearby retailers to be notified.</span>
              </label>

              <button
                onClick={send}
                disabled={busy || !actively || validRooms.length === 0}
                className="w-full h-12 rounded-full bg-[#E05915] hover:bg-[#c24b10] text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="request-send-btn"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send request to nearby retailers
              </button>
              <p className="text-[11px] text-slate-400 text-center">Retailers never see your name, email or phone — only the room size, type and approximate area.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
